/*=========================================================================================
  File Name: referralsMixin.js
  Description: Shared helpers for the three Referrals pages (Collect / Settings / View).
  ----------------------------------------------------------------------------------------
==========================================================================================*/

export default {
  methods: {
    statusPill (status) {
      const map = {
        invited:  { label: 'Invited',  bg: '#e8f0fe', fg: '#1a73e8' },
        redeemed: { label: 'Redeemed', bg: '#fff4e5', fg: '#b26a00' },
        rewarded: { label: 'Rewarded', bg: '#e6f4ea', fg: '#1e7e34' },
        rejected: { label: 'Rejected', bg: '#fdecea', fg: '#c0392b' }
      }
      const s = map[status] || { label: status, bg: '#eee', fg: '#555' }
      return `<span style="background:${s.bg};color:${s.fg};padding:2px 10px;border-radius:12px;font-size:12px;font-weight:600;">${s.label}</span>`
    },
    // PHASE 3.1 — derive the displayed lead status, with the reward state machine WINNING over the
    // stored leadStatus field. The lookup-console "Confirm" path and any legacy doc advance the
    // reward `status` without touching leadStatus; deriving terminal states from `status` keeps the
    // two axes from desyncing no matter which path advanced the reward.
    deriveLeadStatus (referral) {
      if (!referral) return 'new'
      if (referral.status === 'rejected') return 'not_interested'
      if (referral.status === 'redeemed' || referral.status === 'rewarded') return 'converted'
      return referral.leadStatus || 'new'
    },
    leadStatusPill (leadStatus) {
      const map = {
        new:            { label: 'New',            bg: '#e8f0fe', fg: '#1a73e8' },
        contacted:      { label: 'Contacted',      bg: '#fff4e5', fg: '#b26a00' },
        converted:      { label: 'Converted',      bg: '#e6f4ea', fg: '#1e7e34' },
        not_interested: { label: 'Not interested', bg: '#fdecea', fg: '#c0392b' }
      }
      const s = map[leadStatus] || { label: leadStatus, bg: '#eee', fg: '#555' }
      return `<span style="background:${s.bg};color:${s.fg};padding:2px 10px;border-radius:12px;font-size:12px;font-weight:600;">${s.label}</span>`
    },
    // how this referral was captured (Phase 3): manual = staff-added, survey = email invite
    // (referralq Email button), link = a shared referral-link claim, group = a group referral
    // (one row per group — the members live on the doc).
    sourceBadge (origin) {
      const map = {
        manual: { label: 'Added by staff', bg: '#eef0f2', fg: '#555' },
        survey: { label: 'Email invite',   bg: '#e8f0fe', fg: '#1a73e8' },
        link:   { label: 'Shared link',    bg: '#e6f4ea', fg: '#1e7e34' },
        group:  { label: 'Group',          bg: '#ede7f6', fg: '#5e35b1' }
      }
      const s = map[origin] || { label: origin || '—', bg: '#eee', fg: '#555' }
      return `<span style="background:${s.bg};color:${s.fg};padding:2px 10px;border-radius:12px;font-size:12px;font-weight:600;">${s.label}</span>`
    },
    notifySuccess (text) {
      this.$vs.notify({ title: 'Done', text, color: 'success', time: 5000, iconPack: 'feather', icon: 'icon-check' })
    },
    notifyError (text) {
      this.$vs.notify({ title: 'Error', text, color: 'danger', time: 6000, iconPack: 'feather', icon: 'icon-alert-circle' })
    },
    notifyWarning (text) {
      // longer display time — warnings carry info the owner may need to act on (e.g. a code to hand over)
      this.$vs.notify({ title: 'Heads up', text, color: 'warning', time: 10000, iconPack: 'feather', icon: 'icon-alert-triangle' })
    },
    // Turn an axios error from a referral endpoint into a message that says what actually went
    // wrong. The dashboard service returns { success:false, error:'<code>' } for thrown codes
    // (see its error middleware); no response at all means the request never reached the server.
    referralErrorText (e, fallback) {
      if (!e || !e.response) return 'Could not reach the server — check your internet connection and try again.'
      const code = e.response.data && e.response.data.error
      const map = {
        'invalid auth': 'Your login session has expired. Please refresh the page and log in again.',
        noaccess: 'Your account doesn’t have access to this business’s referrals.',
        notfound: 'That referral no longer exists. Refresh the list and try again.',
        badstate: 'This referral was already confirmed or rejected — refresh to see its current state.',
        invalidfriendemail: 'The friend’s email address doesn’t look valid. Please correct it and try again.',
        invalidfriendphone: 'The friend’s phone number doesn’t look valid. Please correct it and try again.',
        missingparam: 'Some required information was missing. Refresh the page and try again.',
        noedoc: 'This business could not be found. Refresh the page and try again.',
        codegenfailed: 'We couldn’t generate a unique reward code. Please try again.',
        invalidemail: 'That email address doesn’t look valid. Please correct it and try again.',
        notenabled: 'Your referral program is switched off — turn it on under Settings › Referrals first.',
        linktokengenfailed: 'We couldn’t generate a unique link. Please try again.'
      }
      if (code && map[code]) return map[code]
      const detail = code || (e.response.status ? `error ${e.response.status}` : 'unknown error')
      return `${fallback} (${detail}). Please try again — if it keeps happening, contact support.`
    },
    // Success copy for a "Converted" confirm, driven by the backend's rewardReason so the owner
    // knows whether a referrer reward actually went out and why not when it didn't.
    // ('emailfailed' is handled by the caller as a warning — it needs the referrer code.)
    convertedSuccessText (res) {
      // GROUP REFERRALS Phase 5 — confirming a group redeems the whole group at once, so the
      // copy says so (the backend returns origin === 'group' for group rows).
      if (res && res.origin === 'group') {
        if (res.rewarded) return 'Group confirmed — referrer reward emailed'
        const greason = res.rewardReason
        if (greason === 'alreadyrewarded') return 'Group confirmed. The referrer already received their once-off reward, so no new reward was sent.'
        if (greason === 'onesided') return 'Group confirmed. Referrer rewards are switched off in your settings, so no reward email was sent.'
        if (greason === 'noreward') return 'Group confirmed. You haven’t set a referrer reward, so no reward email was sent.'
        if (greason === 'noreferreremail') return 'Group confirmed. There’s no referrer email on file, so the reward couldn’t be emailed.'
        return 'Group confirmed'
      }
      if (res && res.rewarded) return 'Converted — referrer reward emailed'
      const reason = res && res.rewardReason
      if (reason === 'alreadyrewarded') return 'Converted. The referrer already received their once-off reward, so no new reward was sent.'
      if (reason === 'onesided') return 'Converted. Referrer rewards are switched off in your settings, so no reward email was sent.'
      if (reason === 'noreward') return 'Converted. You haven’t set a referrer reward, so no reward email was sent.'
      if (reason === 'noreferreremail') return 'Converted. There’s no referrer email on file, so the reward couldn’t be emailed.'
      return 'Marked as converted'
    }
  }
}
